export const OFFLINE_CACHE_OWNER_KEY = 'inventree_offline_cache_owner';
export const OFFLINE_CACHE_WRITE_GENERATION_KEY = 'inventree_offline_cache_write_generation';
export const OFFLINE_SESSION_INVALIDATION_KEY = 'inventree_offline_session_invalidation';

const OFFLINE_SESSION_CHANNEL = 'inventree-offline-session';
const sourceId =
    globalThis.crypto?.randomUUID?.()
    ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

let sessionChannel = null;

const storage = () => {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
        return null;
    }
};

const createGeneration = () =>
    globalThis.crypto?.randomUUID?.()
    ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getSessionChannel = () => {
    if (sessionChannel || typeof BroadcastChannel === 'undefined') return sessionChannel;
    try {
        sessionChannel = new BroadcastChannel(OFFLINE_SESSION_CHANNEL);
    } catch {
        sessionChannel = null;
    }
    return sessionChannel;
};

export const getOfflineCacheOwnerId = () => storage()?.getItem(OFFLINE_CACHE_OWNER_KEY) ?? null;

export const setOfflineCacheOwnerId = (ownerId) => {
    const target = storage();
    if (!target) return;
    if (ownerId === null || ownerId === undefined || ownerId === '') {
        target.removeItem(OFFLINE_CACHE_OWNER_KEY);
        return;
    }
    target.setItem(OFFLINE_CACHE_OWNER_KEY, String(ownerId));
};

export const getOfflineCacheWriteGeneration = () =>
    storage()?.getItem(OFFLINE_CACHE_WRITE_GENERATION_KEY) ?? 'legacy';

export const rotateOfflineCacheWriteGeneration = () => {
    const generation = createGeneration();
    storage()?.setItem(OFFLINE_CACHE_WRITE_GENERATION_KEY, generation);
    return generation;
};

/**
 * Tell every other open tab that the authenticated session is being cleared.
 * The shared cache-write generation is rotated by the caller before publishing
 * so an old response is stale even before the other tab handles this event.
 */
export const publishOfflineSessionInvalidation = ({
    generation,
    ownerId = null,
    reason = 'session_cleared',
} = {}) => {
    const event = {
        id: createGeneration(),
        sourceId,
        generation: generation ?? getOfflineCacheWriteGeneration(),
        ownerId: ownerId == null ? null : String(ownerId),
        reason,
        createdAt: Date.now(),
    };
    const encoded = JSON.stringify(event);

    try {
        storage()?.setItem(OFFLINE_SESSION_INVALIDATION_KEY, encoded);
    } catch {
        // BroadcastChannel remains available when storage is unavailable.
    }

    try {
        getSessionChannel()?.postMessage(event);
    } catch {
        // The storage event is the compatibility fallback.
    }

    return event;
};

export const subscribeOfflineSessionInvalidations = (handler) => {
    if (typeof handler !== 'function') return () => {};

    const seen = new Set();
    const deliver = (event) => {
        if (!event || event.sourceId === sourceId || !event.id || seen.has(event.id)) return;
        seen.add(event.id);
        if (seen.size > 100) {
            const oldest = seen.values().next().value;
            seen.delete(oldest);
        }
        handler(event);
    };

    const onStorage = (event) => {
        if (event.key !== OFFLINE_SESSION_INVALIDATION_KEY || !event.newValue) return;
        try {
            deliver(JSON.parse(event.newValue));
        } catch {
            // Ignore malformed or user-edited storage values.
        }
    };

    const channel = getSessionChannel();
    const onMessage = (event) => deliver(event?.data);
    if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
    channel?.addEventListener?.('message', onMessage);

    return () => {
        if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
        channel?.removeEventListener?.('message', onMessage);
    };
};

/**
 * Returns true when any persisted identity conflicts with the user encoded in
 * the verified offline token. A malformed persisted user is treated as an
 * unknown owner and must be cleared before the token can be adopted.
 */
export const hasOfflineIdentityMismatch = ({
    tokenUserId,
    offlineSessionUserId = null,
    cacheOwnerId = null,
    storedUserRaw = null,
    currentUserId = null,
}) => {
    let storedUserId = null;
    let storedUserIdentityUnknown = false;

    if (storedUserRaw) {
        try {
            storedUserId = JSON.parse(storedUserRaw)?.id ?? null;
            storedUserIdentityUnknown = storedUserId == null;
        } catch {
            storedUserIdentityUnknown = true;
        }
    }

    if (storedUserIdentityUnknown) return true;

    return [offlineSessionUserId, cacheOwnerId, storedUserId, currentUserId]
        .filter((id) => id !== null && id !== undefined && id !== '')
        .some((id) => String(id) !== String(tokenUserId));
};
